import React, { useEffect, useRef, useMemo } from 'react'
import { Box, Checkbox, FormControlLabel } from '@mui/material'
import { EditIcon, ParametersIcon } from '../../../../../../components'
import DetailListItem from '../../DetailListItem/DetailListItem'
import {
  ButtonWrapper,
  GroupButton,
  Header,
  List,
  SliderContainer,
  Slider,
  StyledDivider,
  StyledEditIconContainer,
  VerticalLine,
  SliderGroupLines,
} from './DeliveryHeader.styled'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { useDeliveriesService } from '../../../../../../services'
import { useGroupingLogic } from '../../../../hooks/useGroupingLogic'
import useGroupDeliveries from '../../../../dialogs/useGroupDeliveries'
import { Entity, usePermissions } from '../../../../../../utils/hooks'
import { useSelector } from 'react-redux'
import {
  RootState,
  useSetShowDiscarded,
  useSetShowStorage,
} from '../../../../../../store'
import { useSystemSettings } from '../../../../../../utils/hooks/useSystemSettings'

type DeliveryHeaderProps = {
  activeDeliveryId: number
  wheatCodeId: string
  wheatCode?: string
  onDeliveryClick: (deliveryId: number, wheatCodeId: string) => void
}

export const DeliveryHeader = ({
  activeDeliveryId,
  wheatCodeId,
  wheatCode,
  onDeliveryClick,
}: DeliveryHeaderProps) => {
  const { checkPermission } = usePermissions()
  const { setGroupDeliveries, updateGroupDeliveries, getStoredDeliveriesThin } =
    useDeliveriesService()
  const queryClient = useQueryClient()
  const showDiscarded = useSelector(
    (state: RootState) => state.shipments.showDiscarded
  )
  const showStorage = useSelector(
    (state: RootState) => state.shipments.showStorage
  )
  const { isEnabled: displayStorage } = useSystemSettings('SHOW_WHEAT_STORAGE')

  const setShowDiscarded = useSetShowDiscarded()
  const setShowStorage = useSetShowStorage()

  const PAGE_LIMIT = 20

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['fetch-stored-deliveries', wheatCodeId, showDiscarded, showStorage],
      ({ pageParam = 0 }) =>
        getStoredDeliveriesThin(
          wheatCodeId,
          showDiscarded,
          showStorage,
          pageParam,
          PAGE_LIMIT
        ),
      {
        getNextPageParam: (lastPage) => {
          const { offset, limit, total } = lastPage.pagination
          if (offset + limit < total) {
            return offset + limit
          }
          return undefined
        },
      }
    )

  const deliveries = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) || []
  }, [data?.pages])

  const { groupedDeliveries, containerRef, lineStyles } = useGroupingLogic({
    id: Number(wheatCodeId),
    delivery: deliveries,
    orientation: 'horizontal',
  })

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const [openGroupModal, groupModal] = useGroupDeliveries({
    wheatCode,
    wheatCodeId: Number(wheatCodeId),
    onConfirm: async (groupedIds, groupId) => {
      if (groupId) {
        await updateGroupDeliveries(groupedIds, groupId)
      } else {
        await setGroupDeliveries(groupedIds)
      }
      await queryClient.invalidateQueries('fetch-stored-deliveries')
      await queryClient.invalidateQueries('fetch-grouping-deliveries')
    },
  })

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      if (
        container.scrollLeft + container.clientWidth + 100 >=
        container.scrollWidth
      ) {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <Header>
      <ButtonWrapper>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Box>
            <FormControlLabel
              control={<Checkbox />}
              label="Show Discarded"
              checked={showDiscarded}
              onChange={(_selected, checked) => {
                setShowDiscarded(checked)
              }}
            />
          </Box>
          {displayStorage && (
            <Box>
              <FormControlLabel
                control={<Checkbox />}
                label="Show Storage"
                checked={showStorage}
                onChange={(_selected, checked) => {
                  setShowStorage(checked)
                }}
              />
            </Box>
          )}
        </Box>
        <GroupButton
          variantType="secondary"
          size="small"
          startIcon={<ParametersIcon size={20} />}
          onClick={() => openGroupModal()}
          disabled={!checkPermission(Entity.DELIVERY, 'create_group')}
        >
          Group Deliveries
        </GroupButton>
      </ButtonWrapper>

      <SliderContainer ref={scrollContainerRef}>
        <SliderGroupLines>
          {Object.entries(groupedDeliveries).map(([groupId]) => {
            const style = lineStyles[Number(groupId)]
            if (!style) return null

            return (
              <Box key={groupId} position="relative">
                <StyledDivider
                  orientation="horizontal"
                  sx={{
                    left: `${style.left}px`,
                    width: `${style.width}px`,
                  }}
                />
                <StyledEditIconContainer
                  sx={{
                    left: `${Number(style.left) + Number(style.width) / 2}px`,
                  }}
                  onClick={() => openGroupModal(Number(groupId))}
                >
                  <EditIcon size={10} />
                </StyledEditIconContainer>
              </Box>
            )
          })}
        </SliderGroupLines>

        <Slider ref={containerRef}>
          <List>
            {deliveries.map((item) => {
              const deliveryGroupId = item.delivery_group?.id
              const isGrouped =
                deliveryGroupId &&
                groupedDeliveries[deliveryGroupId]?.includes(item.id)

              return (
                <Box key={item.id} position="relative">
                  {isGrouped && (
                    <VerticalLine
                      orientation="vertical"
                      data-divider-id={`divider-${wheatCodeId}-${item.id}`}
                    />
                  )}
                  <DetailListItem
                    date={item.nir?.arrival_date}
                    consistency={item.consistency}
                    active={activeDeliveryId === item.id}
                    onClick={() => onDeliveryClick(item.id, wheatCodeId)}
                    isDiscarded={item.discarded}
                  />
                </Box>
              )
            })}
          </List>
        </Slider>
      </SliderContainer>

      {groupModal}
    </Header>
  )
}
